import React, { useState } from 'react'
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material'
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import { com } from '@eidu/form'
import GroupHeader from './GroupHeader'
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import NonEmptyList = com.quickbirdstudios.nonEmptyCollection.list.NonEmptyList

import SelectorEditor from './SelectorEditor'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import { SelectorType } from './SelectorType'

type AndOrProps = {
  selector: EntitySelector.And | EntitySelector.Or
  entityTypes: readonly EntityType[]
  onChange: (newSelector: EntitySelector.And | EntitySelector.Or | EntitySelector.Not) => void
  selectorType: SelectorType
  showHeader?: boolean
}

type KeyedSelector = {
  id: string
  selector: EntitySelector
}

const AndOrSelector: React.FC<AndOrProps> = ({ selector, entityTypes, onChange, selectorType, showHeader }) => {
  const operator = selector instanceof EntitySelector.And ? 'AND' : 'OR'
  const initialKeyed: KeyedSelector[] = selector.selectors
    .asJsReadonlyArrayView()
    .map((sel: EntitySelector) => ({ id: crypto.randomUUID(), selector: sel }))
  const [keyedChildren, setKeyedChildren] = useState<KeyedSelector[]>(initialKeyed)

  const updateChildren = (newKeyed: KeyedSelector[]) => {
    setKeyedChildren(newKeyed)
    const newChildren = NonEmptyList.fromArray(newKeyed.map((item) => item.selector))
    const newGroup = operator === 'AND' ? new EntitySelector.And(newChildren) : new EntitySelector.Or(newChildren)
    onChange(newGroup)
  }

  return (
    <Paper variant="outlined" sx={{ borderColor: 'grey', borderRadius: 1, padding: 2 }}>
      {showHeader && (
        <GroupHeader
          operator={operator}
          isNegated={false}
          onOperatorChange={(newOp) => {
            const newGroup =
              newOp === 'AND' ? new EntitySelector.And(selector.selectors) : new EntitySelector.Or(selector.selectors)
            onChange(newGroup)
          }}
          onToggleNegation={() => onChange(new EntitySelector.Not(selector))}
        />
      )}
      <Stack>
        {keyedChildren.map((child) => (
          <Stack key={child.id} direction="row" alignItems="center">
            <SelectorEditor
              selector={child.selector}
              onChange={(newSel) => {
                const newKeyed = keyedChildren.map((item) =>
                  item.id === child.id ? { ...item, selector: newSel } : item
                )
                updateChildren(newKeyed)
              }}
              entityTypes={entityTypes}
              selectorType={selectorType}
              enableOr={false}
            />
            <IconButton
              onClick={() => {
                const newKeyed = keyedChildren.filter((item) => item.id !== child.id)
                updateChildren(newKeyed)
              }}
            >
              <RemoveCircleOutline color="error" />
            </IconButton>
          </Stack>
        ))}
        <Box sx={{ justifyContent: 'center' }}>
          <IconButton
            onClick={() => {
              const newChild: KeyedSelector = {
                id: crypto.randomUUID(),
                selector: new EntitySelector.OfType(entityTypes[0].id),
              }
              updateChildren([...keyedChildren, newChild])
            }}
            sx={{ width: 'auto', flexGrow: 0 }}
          >
            <AddCircleOutline color="primary" />
            <Typography variant="caption">Add entity type</Typography>
          </IconButton>
        </Box>
      </Stack>
    </Paper>
  )
}

export default AndOrSelector
